import { useEffect, useMemo, useState } from 'react';
import { BiPlus, BiSearch } from 'react-icons/bi';
import { FaClockRotateLeft } from 'react-icons/fa6';
import { useSearchParams } from 'react-router-dom';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { DateNavPicker } from '@/components/DateNavPicker';
import { Input } from '@/components/Inputs';
import { Tile } from '@/components/Tile/Tile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useCreateEquipmentMutation } from '@/store/equipment/equipment.store';
import {
  useCreateSafetyLogMutation,
  useSafetyLogPdfMutation,
  useSafetyLogsByDateQuery,
  useSafetyLogsQuery,
  useSignSafetyLogMutation,
  useUpdateSafetyLogMutation,
} from '@/store/safety-log/safety-log.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { getBrowserTimeZone } from '@/utils/timezone';
import { toast } from '@/utils/toast';

import { LOG_VIEW_PARAM, useLogView } from '../useLogView';

import { SafetyFooterSection } from './components/SafetyFooterSection';
import { SafetyHeaderFields } from './components/SafetyHeaderFields';
import { SafetyItemsSection } from './components/SafetyItemsSection';
import { SafetyRequirementsNote } from './components/SafetyRequirementsNote';
import { useSafetyEquipment } from './hooks/useSafetyEquipment';
import { useSafetyLogForm } from './hooks/useSafetyLogForm';
import {
  formStateToCreateValues,
  formStateToUpdateValues,
  safetyLogToFormState,
  safetyLogToTemplateFormState,
} from './mappers';
import s from './SafetyLog.module.scss';
import SafetyLogHistory from './SafetyLogHistory/SafetyLogHistory';
import type { SafetyItem } from './types';

const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

// Matches SafetyLogHistory so the list query is served from the same cache.
const RECENT_LOGS_LIMIT = 100;

const isoToParam = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const paramToISO = (param: string | null): string => {
  if (param && DATE_PARAM_RE.test(param)) {
    const d = new Date(`${param}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SafetyLog = () => {
  const isMobile = useIsMobile();
  const vessel = useVessels((s) => s.vessel);
  const { can } = usePermissions();
  const canSign = can(PERMISSIONS.logbook.SafetyLogSign);
  // The same right the equipment screens are gated on — one endpoint, one rule,
  // so the add control is hidden exactly where the write would be refused.
  const canUseEquipment = can(PERMISSIONS.vessel.Equipment);

  const { view, openHistory, openedFromLogsCard, startNewEntry } = useLogView();
  const [historySearch, setHistorySearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const isoDate = useMemo(() => paramToISO(dateParam), [dateParam]);
  const dateKey = useMemo(() => isoToParam(isoDate), [isoDate]);

  const { state, logId, setState, setLogId, prefillForm, resetForm } = useSafetyLogForm(isoDate);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const { data: fetchedLogs, isFetching } = useSafetyLogsByDateQuery(dateKey);
  const { data: recentLogs } = useSafetyLogsQuery(RECENT_LOGS_LIMIT);

  const fetchedLog = fetchedLogs?.[0];

  // Most recent existing log (other than the one for the open date), used to
  // seed the item checklist when starting a new log.
  const lastLog = useMemo(() => {
    const rows = recentLogs?.pages.flatMap((page) => page.items) ?? [];
    return (
      rows
        .filter((row) => isoToParam(row.date) !== dateKey)
        .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null
    );
  }, [recentLogs, dateKey]);
  const { options: equipmentOptions, defaultTypeId } = useSafetyEquipment();
  const createEquipmentMutation = useCreateEquipmentMutation();
  const createMutation = useCreateSafetyLogMutation();
  const updateMutation = useUpdateSafetyLogMutation();
  const signMutation = useSignSafetyLogMutation();
  const pdfMutation = useSafetyLogPdfMutation();

  useEffect(() => {
    if (fetchedLog) {
      prefillForm(safetyLogToFormState(fetchedLog), fetchedLog.id);
    } else if (lastLog) {
      // New log for this date: carry over the item names from the last log,
      // leaving remarks and time empty. logId stays null so saving creates one.
      prefillForm(safetyLogToTemplateFormState(lastLog, isoDate), null);
    } else {
      resetForm(isoDate);
    }
  }, [fetchedLog, lastLog, isoDate, prefillForm, resetForm]);

  const navigateToDate = (iso: string) => {
    const param = isoToParam(iso);
    const today = isoToParam(new Date().toISOString());
    setSearchParams(
      (prev) => {
        if (!param || param === today) prev.delete('date');
        else prev.set('date', param);
        // A picked day is always shown as its page, and this has to be one
        // navigation: a second `setSearchParams` in the same tick would start
        // from the old params and drop the date again.
        prev.delete(LOG_VIEW_PARAM);
        return prev;
      },
      { replace: true },
    );
  };

  const handleStartAdd = () => {
    setEditingId(null);
    setAddingNew(true);
  };

  const handleStartEdit = (id: string) => {
    setAddingNew(false);
    setEditingId(id);
  };

  const handleCancel = () => {
    setAddingNew(false);
    setEditingId(null);
  };

  const handleSaveEdit = (id: string, patch: Partial<SafetyItem>) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    }));
    setEditingId(null);
  };

  const handleAddNew = (values: Omit<SafetyItem, 'id'>) => {
    setState((prev) => ({ ...prev, items: [...prev.items, { id: newId(), ...values }] }));
    setAddingNew(false);
  };

  const handleRemove = (id: string) =>
    setState((prev) => ({ ...prev, items: prev.items.filter((it) => it.id !== id) }));

  /**
   * The rows named through the picker's add control, put into the vessel's
   * Safety Equipment as the log is saved rather than as they are typed — a row
   * abandoned before Save leaves nothing in the inventory behind it.
   *
   * A name the category already holds is adopted rather than added twice, which
   * is also what makes a second Save after a half-finished one harmless. Answers
   * with the equipment id each row ended up on, keyed by row.
   */
  const createPendingEquipment = async (items: SafetyItem[]): Promise<Map<string, string>> => {
    const resolved = new Map<string, string>();
    const pending = items.filter((item) => item.isNewEquipment && item.name.trim());
    if (!pending.length) return resolved;

    if (!defaultTypeId) {
      toast.error('Couldn’t update Safety Equipment. Please try again.');
      return resolved;
    }

    const byName = new Map(
      equipmentOptions.map(({ name, id }) => [name.trim().toLowerCase(), id] as const),
    );
    let failed = false;

    for (const item of pending) {
      const name = item.name.trim();
      const key = name.toLowerCase();
      if (!byName.has(key)) {
        try {
          const { id } = await createEquipmentMutation.mutateAsync({
            typeId: defaultTypeId,
            name,
            installed: null,
            runningHours: null,
            fields: {},
          });
          byName.set(key, id);
        } catch {
          failed = true;
          continue;
        }
      }
      resolved.set(item.id, byName.get(key)!);
    }

    if (failed) toast.error('Couldn’t add every new item. Please try again.');

    return resolved;
  };

  const handleSave = async ({ silent = false } = {}): Promise<string | null> => {
    const resolved = await createPendingEquipment(state.items);
    // A row whose equipment landed is no longer pending, so a re-save neither
    // adds it again nor keeps promising to.
    const adopt = (item: SafetyItem): SafetyItem => {
      const equipmentId = resolved.get(item.id);
      return equipmentId ? { ...item, equipmentId, isNewEquipment: false } : item;
    };
    const nextState = { ...state, items: state.items.map(adopt) };
    if (resolved.size) setState((prev) => ({ ...prev, items: prev.items.map(adopt) }));

    try {
      if (logId) {
        const saved = await updateMutation.mutateAsync(formStateToUpdateValues(logId, nextState));
        setLogId(saved.id);
        if (!silent) toast.success('Safety log saved');
        return saved.id;
      }
      const created = await createMutation.mutateAsync(formStateToCreateValues(nextState));
      setLogId(created.id);
      if (!silent) toast.success('Safety log saved');
      return created.id;
    } catch {
      toast.error('Couldn’t save the safety log. Please try again.');
      return null;
    }
  };

  const handleSign = async () => {
    // Persist any pending edits first so the signed record matches the form.
    const id = await handleSave({ silent: true });
    if (!id) return;
    try {
      await signMutation.mutateAsync({ id });
      toast.success('Safety log signed');
    } catch {
      toast.error('Couldn’t sign the safety log. Please try again.');
    }
  };

  const handleExport = async () => {
    if (!logId) return;
    const blob = await pdfMutation.mutateAsync({ id: logId, timezone: getBrowserTimeZone() });
    await downloadBlob(blob, `safety-log-${dateKey}.pdf`);
  };

  // Adding the new equipment is part of the save, so the button stays busy for it.
  const isSaving =
    createMutation.isPending || updateMutation.isPending || createEquipmentMutation.isPending;
  const isSigning = signMutation.isPending || isSaving;
  const isExporting = pdfMutation.isPending;

  const shipName = fetchedLog?.vesselName || vessel?.vessel_name || '';
  const registrationNumber = vessel?.registration_number || '';
  const onImoNumber = vessel?.structure_number_value || '';

  return (
    <>
      <div className={s.toolbar}>
        <div className={s.toolbarControls}>
          {view === 'form' && (
            <ButtonOutlinedSmall onClick={openHistory}>
              <FaClockRotateLeft />
              History
            </ButtonOutlinedSmall>
          )}
          {!isMobile && view === 'form' && (
            <DateNavPicker value={state.date} onChange={navigateToDate} />
          )}
          {view === 'form' && (
            <div className={s.toolbarActions}>
              <ButtonOutlinedSmall
                onClick={() => void handleExport()}
                disabled={isExporting || !logId}
              >
                {isExporting ? 'Exporting…' : 'Export'}
              </ButtonOutlinedSmall>
              <ButtonSmall onClick={() => void handleSave()} disabled={isSaving || isFetching}>
                {isSaving ? 'Saving…' : 'Save'}
              </ButtonSmall>
            </div>
          )}
        </div>
        {view === 'history' && (
          <Input
            placeholder="Search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            startIcon={<BiSearch size={18} />}
            className={s.searchInput}
          />
        )}
        {openedFromLogsCard && (
          <ButtonSmall onClick={startNewEntry} aria-label="Add Entry">
            <BiPlus size={20} aria-hidden="true" />
          </ButtonSmall>
        )}
      </div>

      {isMobile && view === 'form' && (
        <DateNavPicker
          value={state.date}
          onChange={navigateToDate}
          className={s.mobileDatePicker}
        />
      )}

      {view === 'history' && (
        <SafetyLogHistory search={historySearch} onSelectLog={navigateToDate} />
      )}

      {view === 'form' && (
        <div className={s.content}>
          <div className={s.sectionsList}>
            <Tile>
              <SafetyHeaderFields
                shipName={shipName}
                registrationNumber={registrationNumber}
                onImoNumber={onImoNumber}
              />
            </Tile>

            <Tile>
              <SafetyRequirementsNote />
            </Tile>

            <SafetyItemsSection
              items={state.items}
              editingId={editingId}
              addingNew={addingNew}
              equipmentOptions={equipmentOptions}
              canUseEquipment={canUseEquipment}
              onStartAdd={handleStartAdd}
              onStartEdit={handleStartEdit}
              onCancel={handleCancel}
              onSaveEdit={handleSaveEdit}
              onAddNew={handleAddNew}
              onRemove={handleRemove}
            />

            <Tile>
              <SafetyFooterSection
                date={state.date}
                captainName={fetchedLog?.responsibleName ?? ''}
                captainSignedAt={fetchedLog?.responsibleSignedAt ?? ''}
                canSign={canSign}
                canSubmitSign={!isFetching}
                isSigning={isSigning}
                onSign={() => void handleSign()}
              />
            </Tile>
          </div>
        </div>
      )}
    </>
  );
};

export default SafetyLog;
